import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Mic, Square, X } from "lucide-react";
import api from "../../api/api";
import { beginVoiceRecording, encodeVoiceWav, MAX_VOICE_SECONDS } from "./voiceRecording";
import { voiceCopy } from "./voiceCopy";
import styles from "./AssistantVoice.module.css";

export default function AssistantVoice({ onTranscript, onBusyChange, disabled = false, language = "fr", resetKey }) {
  const copy = voiceCopy(language), [phase, setPhase] = useState("idle"), [seconds, setSeconds] = useState(0), [error, setError] = useState(null);
  const session = useRef(null), chunks = useRef([]), sampleRate = useRef(16000), frames = useRef(0), timer = useRef(null);
  const generation = useRef(0), controller = useRef(null), callback = useRef(onTranscript), busyCallback = useRef(onBusyChange), finishing = useRef(false);
  const anchor = useRef(null), [position, setPosition] = useState({ left: 16, bottom: 16 });
  callback.current = onTranscript; busyCallback.current = onBusyChange;
  const release = () => { clearInterval(timer.current); session.current?.stop(); session.current = null; };
  const cancel = () => {
    generation.current++; release(); chunks.current = []; frames.current = 0; controller.current?.abort();
    finishing.current = false; setPhase("idle"); setError(null); busyCallback.current?.(false);
  };
  useEffect(() => { cancel(); return () => { generation.current++; release(); chunks.current = []; controller.current?.abort(); busyCallback.current?.(false); }; }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phase === "idle") return undefined;
    const escape = (event) => { if (event.key === "Escape") { event.preventDefault(); cancel(); anchor.current?.focus(); } };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (phase === "idle") return undefined;
    const update = () => {
      const rect = anchor.current?.getBoundingClientRect(); if (!rect) return;
      const width = Math.min(310, window.innerWidth - 32);
      setPosition({ left: Math.max(16, Math.min(rect.right - width, window.innerWidth - width - 16)),
        bottom: Math.max(16, Math.min(window.innerHeight - rect.top + 12, window.innerHeight - 210)) });
    };
    update(); window.addEventListener("resize", update); window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [phase]);

  const finish = async (ticket) => {
    if (finishing.current || ticket !== generation.current) return;
    finishing.current = true; release(); setPhase("transcribing");
    try {
      const blob = encodeVoiceWav(chunks.current, sampleRate.current); chunks.current = [];
      const form = new FormData(); form.append("file", blob, "dictation.wav");
      controller.current = new AbortController();
      const { data } = await api.post("/assistant/voice/transcriptions", form, { timeout: 45000, signal: controller.current.signal });
      if (ticket !== generation.current) return;
      if (!data?.text) throw new Error("voice_no_speech");
      callback.current(data.text, data.transcription_id); setPhase("ready");
    } catch (e) {
      if (ticket !== generation.current) return;
      setError(e.response?.data?.message || e.message || e.name); setPhase("error");
    } finally {
      if (ticket === generation.current) { finishing.current = false; busyCallback.current?.(false); chunks.current = []; }
    }
  };
  const start = async () => {
    if (disabled || ["requesting", "transcribing"].includes(phase)) return;
    if (phase === "recording") { finish(generation.current); return; }
    const ticket = ++generation.current;
    setError(null); setSeconds(0); setPhase("requesting"); busyCallback.current?.(true); chunks.current = []; frames.current = 0; finishing.current = false;
    try {
      const capture = await beginVoiceRecording((chunk, rate) => {
        if (ticket !== generation.current || finishing.current) return;
        sampleRate.current = rate;
        const remaining = rate * MAX_VOICE_SECONDS - frames.current;
        const bounded = chunk.subarray(0, Math.max(0, remaining));
        chunks.current.push(bounded); frames.current += bounded.length;
        if (frames.current >= rate * MAX_VOICE_SECONDS) finish(ticket);
      });
      if (ticket !== generation.current) { capture.stop(); return; }
      session.current = capture; sampleRate.current = capture.sampleRate; setPhase("recording");
      const began = Date.now();
      timer.current = setInterval(() => {
        const elapsed = Math.min(MAX_VOICE_SECONDS, Math.floor((Date.now() - began) / 1000)); setSeconds(elapsed);
        if (elapsed >= MAX_VOICE_SECONDS) finish(ticket);
      }, 200);
    } catch (e) {
      if (ticket !== generation.current) return;
      release(); setError(e.name === "NotAllowedError" || e.name === "NotFoundError" ? e.name : e.message); setPhase("error"); busyCallback.current?.(false);
    }
  };
  const busy = ["requesting", "recording", "transcribing"].includes(phase), spinning = ["requesting", "transcribing"].includes(phase);
  return <div className={styles.root}>
    <button type="button" ref={anchor} className={`${styles.mic} ${phase === "recording" ? styles.recording : ""}`}
      onClick={start} disabled={disabled || spinning} aria-label={phase === "recording" ? copy.stop : copy.start}
      title={phase === "recording" ? copy.stop : `${copy.start} · 60 s`} aria-pressed={phase === "recording"}>
      {spinning ? <Loader2 size={19} className={styles.spin} aria-hidden="true" /> : phase === "recording" ? <Square size={17} fill="currentColor" aria-hidden="true" /> : <Mic size={20} aria-hidden="true" />}
    </button>
    {phase !== "idle" && createPortal(<div style={position} dir={String(language).startsWith("ar") ? "rtl" : "ltr"} className={`${styles.panel} ${error ? styles.error : ""}`}>
      <div className={styles.head}><span role="status">{error ? copy.error : phase === "ready" ? copy.readyTitle : copy[phase]}</span>
        <button type="button" className={styles.close} onClick={cancel} aria-label={busy ? copy.cancel : copy.close}><X size={17} aria-hidden="true" /></button></div>
      {phase === "recording" && <><progress max={MAX_VOICE_SECONDS} value={seconds} aria-label={copy.recording} /><span className={styles.time}>{seconds} / 60 {copy.seconds}</span></>}
      {error ? <p role="alert">{copy[error] || copy.voice_unavailable}</p> : <p>{phase === "ready" ? copy.ready : copy.hint}</p>}
    </div>, document.body)}
  </div>;
}
